/*
 This file is part of GNU Taler
 (C) 2021-2024 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.

 GNU Taler is distributed in the hope that it will be useful, but WITHOUT ANY
 WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 A PARTICULAR PURPOSE.  See the GNU General Public License for more details.

 You should have received a copy of the GNU General Public License along with
 GNU Taler; see the file COPYING.  If not, see <http://www.gnu.org/licenses/>
 */

/**
 *
 * @author Christian Blättler
 */

import {
  AbsoluteTime,
  assertUnreachable,
  Duration,
  HttpStatusCode,
  TalerMerchantApi,
} from "@gnu-taler/taler-util";
import {
  ButtonBetterBulma,
  LocalNotificationBannerBulma,
  useLocalNotificationBetter,
  useTranslationContext,
} from "@gnu-taler/web-util/browser";
import { h } from "preact";
import { useState } from "preact/hooks";
import {
  FormErrors,
  FormProvider,
} from "../../../../components/form/FormProvider.js";
import { Input } from "../../../../components/form/Input.js";
import { InputDate } from "../../../../components/form/InputDate.js";
import { InputDurationSelector } from "../../../../components/form/InputDurationSelector.js";
import { useSessionContext } from "../../../../context/session.js";
import { undefinedIfEmpty } from "../../../../utils/table.js";
import {
  datetimeFormatForPreferences,
  usePreference,
} from "../../../../hooks/preference.js";
import { addDays, addMonths, endOfMonth, format, startOfMonth } from "date-fns";
import { Tooltip } from "../../../../components/Tooltip.js";
import { InputDurationDropdown } from "../../../../components/form/InputDurationDropdown.js";

const TALER_SCREEN_ID = 70;

type Entity = TalerMerchantApi.TokenFamilyUpdateRequest;

interface Props {
  onUpdated: () => void;
  onBack?: () => void;
  tokenFamily: TalerMerchantApi.TokenFamilyDetails;
}

const today = new Date();
const nextMonth = addMonths(today, 1);
const firstDayNextMonth = startOfMonth(nextMonth);
const lastDayNextMonth = endOfMonth(nextMonth);
const afterLastDayNextMonth = addDays(lastDayNextMonth, 1);
const firstDayNextMonthAnd30Days = addDays(firstDayNextMonth, 30);

export function UpdatePage({ onUpdated, onBack, tokenFamily }: Props) {
  const [value, valueHandler] = useState<Partial<Entity>>(tokenFamily);
  const { i18n } = useTranslationContext();

  const errors = undefinedIfEmpty<FormErrors<Entity>>({
    name: !value.name ? i18n.str`Required` : undefined,
    description: !value.description ? i18n.str`Required` : undefined,
    valid_after:
      !value.valid_after || value.valid_after.t_s === "never"
        ? i18n.str`Required`
        : undefined,
    valid_before: !value.valid_before
      ? i18n.str`Required`
      : value.valid_after &&
          AbsoluteTime.cmp(
            AbsoluteTime.fromProtocolTimestamp(value.valid_after),
            AbsoluteTime.fromProtocolTimestamp(value.valid_before),
          ) > 0
        ? i18n.str`Expiration should be after start date`
        : undefined,
    duration: !value.duration ? i18n.str`Required` : undefined,
    // validity_granularity: !value.validity_granularity
    //   ? i18n.str`Required`
    //   : value.duration &&
    //       Duration.cmp(
    //         Duration.fromTalerProtocolDuration(value.duration),
    //         Duration.fromTalerProtocolDuration(value.validity_granularity),
    //       ) < 1
    //     ? i18n.str`Granularity can't be greater than duration.`
    //     : undefined,
    // kind: !value.kind ? i18n.str`Required` : undefined,
  });
  const { state: session, lib } = useSessionContext();
  const [notification, safeFunctionHandler] = useLocalNotificationBetter();

  const hasErrors = errors !== undefined;

  const update = safeFunctionHandler(
    i18n.str`update token family`,
    lib.instance.updateTokenFamily.bind(lib.instance),
    !session.token || !!errors
      ? undefined
      : [session.token, tokenFamily.slug, value as Entity],
  );
  update.onSuccess = onUpdated;
  update.onFail = (fail) => {
    switch (fail.case) {
      case HttpStatusCode.Unauthorized:
        return i18n.str`Unauthorized`;
      case HttpStatusCode.NotFound:
        return i18n.str`Not found`;
      default:
        assertUnreachable(fail);
    }
  };

  const [preferences] = usePreference();

  return (
    <div>
      <LocalNotificationBannerBulma notification={notification} />
      <section class="section">
        <section class="hero is-hero-bar">
          <div class="hero-body">
            <div class="level">
              <div class="level-left">
                <div class="level-item">
                  <span class="is-size-4">
                    <i18n.Translate>
                      Token Family: <b>{tokenFamily.name}</b>
                    </i18n.Translate>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </section>
        <hr />

        <section class="section is-main-section">
          <div class="columns">
            <div class="column is-four-fifths">
              <FormProvider<Entity>
                name="token_family"
                errors={errors}
                object={value}
                valueHandler={valueHandler}
              >
                <Input<Entity>
                  name="name"
                  inputType="text"
                  label={i18n.str`Name`}
                  tooltip={i18n.str`User-readable token family name`}
                />
                <Input<Entity>
                  name="description"
                  inputType="multiline"
                  label={i18n.str`Description`}
                  tooltip={i18n.str`Token family description for customers`}
                />
                <InputDate<Entity>
                  name="valid_after"
                  label={i18n.str`Start Date`}
                  tooltip={i18n.str`The first day the coupon/subscription can be used.`}
                  help={i18n.str`If set to ${format(
                    firstDayNextMonth,
                    datetimeFormatForPreferences(preferences),
                  )}, it cannot be used before this date.`}
                  withNever
                  useProtocolTimestamp
                />
                <InputDate<Entity>
                  name="valid_before"
                  label={i18n.str`Expiration Date`}
                  tooltip={i18n.str`The last day the coupon/subscription can be used.`}
                  help={i18n.str`If set to ${format(
                    lastDayNextMonth,
                    datetimeFormatForPreferences(preferences),
                  )}, they are no longer valid from ${format(
                    afterLastDayNextMonth,
                    datetimeFormatForPreferences(preferences),
                  )}.`}
                  withNever
                  useProtocolTimestamp
                />
                <InputDurationDropdown<Entity>
                  name="duration"
                  label={i18n.str`Duration`}
                  tooltip={i18n.str`How long the coupon/subscription remains valid after being activated.`}
                  help={i18n.str`If you activate it on ${format(
                    firstDayNextMonth,
                    datetimeFormatForPreferences(preferences),
                  )} with duration of 30 days, it remains valid until ${format(
                    firstDayNextMonthAnd30Days,
                    datetimeFormatForPreferences(preferences),
                  )}.`}
                  withForever
                  useProtocolDuration
                />
                <div class="buttons is-right mt-5">
                  {onBack && (
                    <button type="button" class="button" onClick={onBack}>
                      <i18n.Translate>Cancel</i18n.Translate>
                    </button>
                  )}
                  <Tooltip
                    text={
                      hasErrors
                        ? i18n.str`Please complete the marked fields`
                        : i18n.str`Confirm operation`
                    }
                  >
                    <ButtonBetterBulma type="submit" onClick={update}>
                      <i18n.Translate>Confirm</i18n.Translate>
                    </ButtonBetterBulma>
                  </Tooltip>
                </div>
              </FormProvider>
            </div>
          </div>
        </section>
      </section>
    </div>
  );
}
